import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, of, switchMap, takeUntil, tap } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { helper } from 'src/environments/helper';
import { CereriService } from '../cereri.service';
import { UsersService } from 'src/app/services/users.service';
import { NotificationService } from 'src/app/services/notification.service';
import { CommonService } from 'src/app/services/common.service';
import { GestiuneAjutoareService } from '../../gestiune-ajutoare/gestiune-ajutoare.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-cereri-aloca-verificator',
    templateUrl: './cereri-aloca-verificator.component.html',
    styleUrls: ['./cereri-aloca-verificator.component.scss'],
})
export class CereriAlocaVerificatorComponent {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    helper = helper;

    isLoading = false;

    dgaspc_id = environment.interop.dgaspcId;

    private destroy$ = new Subject<void>();

    directiiActive: Generic[] = [];
    filteredDirectii: Generic[] = [];
    directiiControl = new FormControl();

    compartimenteActive: Generic[] = [];
    filteredCompartimente: Generic[] = [];
    compartimenteControl = new FormControl();

    usersActive: Generic[] = [];
    filteredUsers: Generic[] = [];
    usersControl = new FormControl();

    form: FormGroup = new FormGroup({
        directie_id: new FormControl(this.dgaspc_id, [Validators.required]),
        compartiment_id: new FormControl(''),
        operator: new FormControl('', [Validators.required]),
    });

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: number[],
        private dialogRef: MatDialogRef<CereriAlocaVerificatorComponent>,
        private commonService: CommonService,
        private cereriService: CereriService,
        private usersService: UsersService,
        private notificationService: NotificationService,
        private gestiuneService: GestiuneAjutoareService,
    ) {}

    ngOnInit(): void {
        this.loadDirectiiActive();
        this.loadCompartimenteActive(this.dgaspc_id);
        this.loadUsersActive();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({ success: false });
    }

    renderFieldsSearch(): void {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$), distinctUntilChanged(),
            switchMap((v) => (!v || v.length < 1) ? (this.filteredDirectii = this.directiiActive, of(this.directiiActive)) : this.filterDirectii(v)),
            tap((data: Generic[]) => { this.filteredDirectii = data; }), catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$), distinctUntilChanged(),
            switchMap((v) => (!v || v.length < 1) ? (this.filteredCompartimente = this.compartimenteActive, of(this.compartimenteActive)) : this.filterCompartimente(v)),
            tap((data: Generic[]) => { this.filteredCompartimente = data; }), catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.destroy$), distinctUntilChanged(),
            switchMap((v) => (!v || v.length < 1) ? (this.filteredUsers = this.usersActive, of(this.usersActive)) : this.filterOperatori(v)),
            tap((data: Generic[]) => { this.filteredUsers = data; }), catchError(() => of([]))
        ).subscribe();
    }

    loadDirectiiActive(): void {
        this.isLoading = true;
        this.commonService.getDirectiiActive().pipe(finalize(() => (this.isLoading = false))).subscribe({
            next: (results) => { this.directiiActive = results; this.filteredDirectii = [...results]; },
            error: () => { this.filteredDirectii = []; },
        });
    }

    onDirectieClosed(): void {
        const valid = this.filteredDirectii.some(d => d.id === this.form.get('directie_id')?.value);
        if (!valid) { this.directiiControl.setValue(null); this.filteredDirectii = this.directiiActive; this.destroy$.next(); this.destroy$.complete(); this.destroy$ = new Subject<void>(); this.renderFieldsSearch(); }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].reset();
        this.form.controls['operator'].reset();
        if (directieId > 0) { this.loadCompartimenteActive(directieId); this.loadUsersActive(); }
        else { this.compartimenteActive = []; this.filteredCompartimente = []; this.usersActive = []; this.filteredUsers = []; }
    }

    loadCompartimenteActive(directieId?: number): void {
        this.isLoading = true;
        const obs$ = directieId ? this.cereriService.getCompartimenteByDirectieId(directieId) : this.cereriService.getCompartimenteActive();
        obs$.pipe(finalize(() => (this.isLoading = false))).subscribe({
            next: (data) => { this.compartimenteActive = data; this.filteredCompartimente = [...data]; },
            error: () => { this.filteredCompartimente = []; },
        });
    }

    onCompartimentClosed(): void {
        const valid = this.filteredCompartimente.some(c => c.id === this.form.get('compartiment_id')?.value);
        if (!valid) { this.compartimenteControl.setValue(null); this.filteredCompartimente = this.compartimenteActive; this.destroy$.next(); this.destroy$.complete(); this.destroy$ = new Subject<void>(); this.renderFieldsSearch(); }
    }

    onCompartimentChange(compartimentId: number): void {
        this.form.controls['operator'].reset();
        if (compartimentId > 0) { this.loadUsersActive(); }
        else { this.usersActive = []; this.filteredUsers = []; }
    }

    loadUsersActive(): void {
        this.isLoading = true;
        this.usersService.getUsers().pipe(finalize(() => (this.isLoading = false))).subscribe({
            next: (data) => { this.usersActive = data; this.filteredUsers = [...data]; },
            error: () => { this.filteredUsers = []; },
        });
    }

    onUsersClosed(): void {
        const valid = this.filteredUsers.some(u => u.id === this.form.get('operator')?.value);
        if (!valid) { this.usersControl.setValue(null); this.filteredUsers = this.usersActive; this.destroy$.next(); this.destroy$.complete(); this.destroy$ = new Subject<void>(); this.renderFieldsSearch(); }
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(s: string): Observable<Generic[]> { const n = this.normalizeString(s); return of(this.directiiActive.filter(d => this.normalizeString(d.name).includes(n))); }
    filterCompartimente(s: string): Observable<Generic[]> { const n = this.normalizeString(s); return of(this.compartimenteActive.filter(c => this.normalizeString(c.name).includes(n))); }
    filterOperatori(s: string): Observable<Generic[]> { const n = this.normalizeString(s); return of(this.usersActive.filter(u => this.normalizeString(u.name).includes(n))); }

    submitForm() {
        this.isLoading = true;

        const userId = this.form.get('operator')?.value;
        const userName = this.usersActive.find(u => u.id === userId)?.name || '';

        this.gestiuneService
            .massAssignVerificareVenituri({
                cerere_ids: this.data,
                user_id: userId,
                user_name: userName,
            })
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => { this.dialogRef.close({ success: true }); },
                error: (err) => {
                    let msg;
                    try { msg = err.error?.message || err.error?.errors?.message?.[0]; } catch { msg = helper.dialogConfig.generalMessages.error; }
                    this.notificationService.warningSwal(this.errorTitle, msg || helper.dialogConfig.generalMessages.error, this.errorIcon);
                },
            });
    }
}
